import { Formik, useFormikContext } from "formik";
import * as yup from "yup";

import { PracticeTimezone } from "@elation/api-client/src/stitch-web/models/practiceTimezone";

import { LoginLayout } from "@src/deprecated/pages/LoginLayout";
import { PRODUCT_NAME } from "@src/shared/brand";
import { FormikInput } from "@src/shared/components/forms/FormikInput";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";

import { AccountCreate } from "./CreateAccount.types";

type FormValues = Required<AccountCreate>;

const FALLBACK_TIMEZONE = PracticeTimezone["America/Chicago"];
const PRACTICE_TIMEZONES = new Set<string>(Object.values(PracticeTimezone));

function getDefaultTimezone() {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return PRACTICE_TIMEZONES.has(timezone) ? timezone : FALLBACK_TIMEZONE;
}

const validationSchema = yup.object<FormValues>({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(10, "Password must be at least 10 characters")
    .test(
      "number-or-symbol",
      "Password must include at least 1 number or symbol",
      (value) => (value ? /[0-9]|[^A-Za-z0-9\s]/.test(value) : true),
    )
    .test(
      "no-whitespace",
      "Password cannot contain whitespace characters",
      (value) => (value ? !/\s/.test(value) : true),
    ),
  timezone: yup
    .mixed()
    .oneOf(Object.values(PracticeTimezone), "Invalid timezone")
    .required("Timezone is required"),
});

export interface CreateAccountPageProps {
  onSubmit: (values: AccountCreate) => Promise<void>;
  onCancel?: () => void;
  error?: string | null;
}

const CreateAccountActions = ({ onCancel }: { onCancel?: () => void }) => {
  const { isSubmitting } = useFormikContext<FormValues>();

  return (
    <HorizontalFlexbox gap="sm" className="flex-wrap justify-end pt-1">
      {onCancel ? (
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      ) : null}
      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Creating account..." : "Create account"}
      </Button>
    </HorizontalFlexbox>
  );
};

export const CreateAccountPage = ({
  onSubmit,
  onCancel,
  error = null,
}: CreateAccountPageProps) => {
  const defaultTimezone = getDefaultTimezone();

  const handleSubmit = async (values: FormValues) => {
    const cleanedValues: AccountCreate = {
      email: values.email.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      password: values.password,
      timezone: values.timezone.trim(),
    };

    await onSubmit(cleanedValues);
  };

  const initialValues: FormValues = {
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    timezone: defaultTimezone,
  };

  return (
    <LoginLayout>
      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange
      >
        {({ handleSubmit }) => (
          <VerticalFlexbox
            as="form"
            gap="md"
            method="post"
            onSubmit={handleSubmit}
            className="w-full"
          >
            <VerticalFlexbox gap="xs">
              <HeaderText tag="h1" className="text-3xl">
                Create Account
              </HeaderText>
              <Text sizing="sm" appearance="subdued">
                Set up your {PRODUCT_NAME.singular} account with the essentials.
              </Text>
              <Text sizing="sm" appearance="subdued">
                Account creation may take up to 30 seconds.
              </Text>
            </VerticalFlexbox>

            {error ? (
              <Text
                appearance="danger"
                role="alert"
                sizing="sm"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2"
              >
                {error || "An error occurred"}
              </Text>
            ) : null}

            <VerticalFlexbox gap="md">
              <HorizontalFlexbox
                gap="sm"
                className="flex-wrap sm-stitch:flex-nowrap"
              >
                <FormikInput name="firstName" label="First Name" required />
                <FormikInput name="lastName" label="Last Name" required />
              </HorizontalFlexbox>
              <FormikInput
                name="email"
                label="Email"
                type="email"
                autoComplete="email"
                required
              />
              <FormikInput
                name="password"
                label="Password"
                type="password"
                autoComplete="new-password"
                hint="Minimum 10 characters with at least 1 number or symbol"
                required
              />
            </VerticalFlexbox>

            <CreateAccountActions onCancel={onCancel} />
          </VerticalFlexbox>
        )}
      </Formik>
    </LoginLayout>
  );
};
